import { useAppSelector } from '@/app/reduxHooks'

import { closeOverlay, type DeletePipelineOverlayData } from '@/features/adp/state/overlaySlice'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/ui/alert-dialog'
import { useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { toast } from 'sonner'
import { useUpdateDataFlowMutation } from '../../mutations/usePipelineMutations'

function isDeletePipelineOverlayData(value: unknown): value is DeletePipelineOverlayData {
  if (!value || typeof value !== 'object') return false
  return 'pipelineId' in value || 'pipelineName' in value || 'source' in value
}

export function DeletePipelineDialog() {
  const dispatch = useDispatch()
  const { isOpen, type, data } = useAppSelector((state) => state.overlay)
  const { dataFlowId, pipelineName } = useAppSelector((state) => state.pipeline)
  const overlayData = isDeletePipelineOverlayData(data) ? data : null
  const updateDataFlowRecord = useUpdateDataFlowMutation();
  const queryClient = useQueryClient()

  const isOpenForDelete = isOpen && type === 'delete-pipeline-dialog'

  const handleClose = () => {
    dispatch(closeOverlay())
  }

  const handleConfirmDelete = async () => {
    const pipelineId = overlayData?.pipelineId?.trim()
    const deletedPipelineName = overlayData?.pipelineName?.trim()
    if (!pipelineId) {
      toast.error('Invalid pipeline id for delete.')
      dispatch(closeOverlay())
      return
    }

    const loadingToastId = toast.loading('Deleting pipeline...')
    try {
      await updateDataFlowRecord.mutateAsync({
        dataFlowId: pipelineId,
        payload: { is_active: false },
      })
      toast.dismiss(loadingToastId)
      toast.success('Pipeline deleted successfully.')
      await queryClient.invalidateQueries({ queryKey: ['adp', 'pipeline-lists'] })
      const currentPipelineId = dataFlowId == null ? null : String(dataFlowId)
      const currentPipelineName = pipelineName.trim()
      const isCanvasDelete = overlayData?.source === 'canvas'
      const isCurrentPipelineById = currentPipelineId === pipelineId
      const isCurrentPipelineByName =
        Boolean(deletedPipelineName) && deletedPipelineName === currentPipelineName

      if (isCanvasDelete || isCurrentPipelineById || isCurrentPipelineByName) {
        // Logic to be added
      }
      dispatch(closeOverlay())
    } catch {
      toast.dismiss(loadingToastId)
      toast.error('Failed to delete pipeline.')
      dispatch(closeOverlay())
    }
  }

  return (
    <AlertDialog open={isOpenForDelete} onOpenChange={(open) => { if (!open) handleClose() }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Pipeline</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete{overlayData?.pipelineName ? ` "${overlayData.pipelineName}"` : ''}? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleClose}>No</AlertDialogCancel>
          <AlertDialogAction onClick={() => void handleConfirmDelete()}>
            Yes
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
